import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { DEAL_TYPES, breakevenSet, formatCurrencyInput, sanitizeCurrencyInput } from './dealParams';
import { REVENUE_BASE_OPTIONS, RevenueBaseTooltip } from '../../../constants/revenueBase';

const CURRENCY_CODE = 'USD';

const CARDS = [
  { value: DEAL_TYPES.FLAT, title: 'Flat', subtitle: 'Fixed guarantee, no upside' },
  { value: DEAL_TYPES.DOOR, title: 'Door deal', subtitle: '% of the door after expenses' },
  { value: DEAL_TYPES.PLUS, title: 'Plus', subtitle: 'Guarantee + % over breakeven' },
  { value: DEAL_TYPES.VERSUS, title: 'Versus', subtitle: 'Greater of guarantee or % after expenses' },
];

const BASIS_LABELS = {
  gross: 'Gross (before expenses)',
  net_gross: 'Net Gross (before expenses)',
};

const defaultBasisLabel = (dealType, breakeven) => {
  if (dealType !== DEAL_TYPES.PLUS) return 'Default (after expenses)';
  if (breakevenSet(breakeven)) return 'Default (gross over breakeven)';
  return 'Default (after expenses + guarantee)';
};

const basisOptions = (dealType, breakeven) => REVENUE_BASE_OPTIONS.map((option) => ({
  value: option.value,
  label: option.value === 'default' ? defaultBasisLabel(dealType, breakeven) : (BASIS_LABELS[option.value] || option.label),
}));

const showsGuarantee = (t) => t !== DEAL_TYPES.DOOR;
const showsPercentage = (t) => t !== DEAL_TYPES.FLAT;
const showsBreakeven = (t) => t === DEAL_TYPES.PLUS;

// Reuses the Offers V3 deal-terms field styling (.deal-terms-field*): a bordered
// input wrap with affixes. Base styles live in simpleOfferLayout.css.
//
// `currency` fields carry a fixed "$" prefix and "USD" suffix and show the value
// grouped to two decimals (25,000.00) while idle, falling back to the raw number
// while focused so it stays easy to edit. Non-currency fields (e.g. percentage)
// keep the plain number input with the given suffix.
const MoneyField = ({ testId, label, value, onChange, currency, suffix }) => {
  const [focused, setFocused] = useState(false);
  const displayValue = currency && !focused ? formatCurrencyInput(value) : value;

  return (
    <label className="deal-terms-field">
      <span className="deal-terms-field__label">{label}</span>
      <span className="deal-terms-field__input-wrap">
        {currency ? <span className="deal-terms-field__prefix">$</span> : null}
        <input
          data-testid={testId}
          className="deal-terms-field__input"
          type={currency ? 'text' : 'number'}
          step={currency ? undefined : '0.01'}
          inputMode="decimal"
          value={displayValue}
          onChange={(e) => onChange(currency ? sanitizeCurrencyInput(e.target.value) : e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
        />
        {currency && <span className="deal-terms-field__suffix">{CURRENCY_CODE}</span>}
        {!currency && suffix ? <span className="deal-terms-field__suffix">{suffix}</span> : null}
      </span>
    </label>
  );
};

MoneyField.propTypes = {
  testId: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  currency: PropTypes.bool,
  suffix: PropTypes.string,
};
MoneyField.defaultProps = { currency: false, suffix: null };

const DealTypeSelector = ({ dealType, guarantee, percentage, breakeven, revenueBase, dealTermsLanguage, onChange, leadingCard }) => (
  <section className="simple-offer__section">
    <h3 className="simple-offer__section-title">DEAL TYPE</h3>
    <div className="simple-offer__deal-grid">
      {leadingCard}
      {CARDS.map((card) => (
        <button
          type="button"
          key={card.value}
          data-testid="deal-type-option"
          className={`simple-offer__deal-card ${dealType === card.value ? 'simple-offer__deal-card--active' : ''}`}
          aria-pressed={dealType === card.value}
          onClick={() => onChange({ dealType: card.value })}
        >
          <span className="simple-offer__deal-card-title">{card.title}</span>
          <span className="simple-offer__deal-card-subtitle">{card.subtitle}</span>
        </button>
      ))}
    </div>

    <div className="simple-offer__deal-inputs">
      {showsGuarantee(dealType) && (
        <MoneyField testId="guarantee-input" label="Guarantee" value={guarantee}
          onChange={(v) => onChange({ guarantee: v })} currency />
      )}
      {showsPercentage(dealType) && (
        <MoneyField testId="percentage-input" label="Percentage" value={percentage}
          onChange={(v) => onChange({ percentage: v })} suffix="%" />
      )}
      {showsBreakeven(dealType) && (
        <MoneyField testId="breakeven-input" label="Breakeven" value={breakeven}
          onChange={(v) => onChange({ breakeven: v })} currency />
      )}
      {showsPercentage(dealType) && (
        <div className="simple-offer__deal-terms-row">
          <label className="deal-terms-field deal-terms-field--half" data-testid="revenue-base-field">
            <span className="deal-terms-field__label">% calculated on<RevenueBaseTooltip /></span>
            <span className="deal-terms-field__input-wrap">
              <select
                data-testid="revenue-base-select"
                className="deal-terms-field__input"
                value={revenueBase || 'default'}
                onChange={(e) => onChange({ revenueBase: e.target.value })}
              >
                {basisOptions(dealType, breakeven).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </span>
          </label>
          <label className="deal-terms-field deal-terms-field--half" data-testid="deal-terms-language-field">
            <span className="deal-terms-field__label">Deal terms wording (optional)</span>
            <span className="deal-terms-field__input-wrap">
              <input
                data-testid="deal-terms-language-input"
                className="deal-terms-field__input"
                type="text"
                placeholder="of ticket sales, minus expenses deducted before split"
                value={dealTermsLanguage || ''}
                onChange={(e) => onChange({ dealTermsLanguage: e.target.value })}
              />
            </span>
          </label>
        </div>
      )}
    </div>
  </section>
);

DealTypeSelector.propTypes = {
  dealType: PropTypes.string.isRequired,
  guarantee: PropTypes.string.isRequired,
  percentage: PropTypes.string.isRequired,
  breakeven: PropTypes.string.isRequired,
  revenueBase: PropTypes.string,
  dealTermsLanguage: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  leadingCard: PropTypes.node,
};

DealTypeSelector.defaultProps = { revenueBase: 'default', dealTermsLanguage: '', leadingCard: null };

export default DealTypeSelector;
